// src/components/music/parts/TrackCard.jsx

import { useState } from 'react';
import { motion } from 'framer-motion';
import { Play, Pause, ShoppingCart, Disc3, Plus, Download, CheckCircle, SkipBack, SkipForward } from 'lucide-react';
import { getTypeLabel } from '../utils/badgeUtils';
import { useAudioPlayerContext } from '../AudioPlayerProvider';

const EQ_BARS = [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7];

const formatTime = (time) => {
  if (!time || isNaN(time) || time < 0) return '0:00';
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

export const TrackCard = ({
  track,
  index,
  isCurrent,
  isPlaying,
  playQueue,
  onPlayPause,
  onPurchase,
  processingPayment,
  groupByAlbum,
  onAddToPlaylist,
  currentUser,
  isPurchased,
  onDownload
}) => {
  const [downloadingId, setDownloadingId] = useState(null);
  const player = useAudioPlayerContext();
  const queueIndex = playQueue.findIndex(t => t.id === track.id);
  const purchased = isPurchased && typeof isPurchased === 'function' ? isPurchased(track.id) : false;

  const handleDownload = async (track) => {
    setDownloadingId(track.id);
    try {
      await onDownload(track);
    } finally {
      setDownloadingId(null);
    }
  };

  const isNowPlaying = isCurrent;
  const progress = isNowPlaying && player.duration ? (player.currentTime / player.duration) * 100 : 0;
  const currentLabel = isNowPlaying ? formatTime(player.currentTime) : '0:00';
  const remainingLabel = isNowPlaying && player.duration
    ? formatTime(Math.max(0, player.duration - player.currentTime))
    : '0:00';

  const handleSeek = (e) => {
    if (!isNowPlaying || !player.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    player.seekTo(pct * player.duration);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: (index % 6) * 0.05 }}
      viewport={{ once: true }}
      className={`flex flex-col gap-3.5 rounded-[28px] p-4 sm:p-5 text-white w-full transition-all duration-300 backdrop-blur-md border ${
        isCurrent 
          ? 'ring-2 ring-[#bd9f67] border-[#bd9f67]/60 bg-[#181a22]' 
          : 'bg-[#121318]/70 border-white/10 hover:border-[#bd9f67]/40 hover:shadow-[0_10px_25px_rgba(189,159,103,0.12)]'
      }`}
      style={{ boxShadow: '0 8px 24px rgba(0,0,0,0.4)', maxWidth: 420 }}
    >
      {/* Type Badge and Track Number */}
      <div className="flex flex-wrap items-center gap-1.5">
        <span
          className={`text-xs font-semibold px-2.5 py-0.5 rounded-full border ${
            isCurrent 
              ? 'bg-[#bd9f67]/20 text-[#bd9f67] border-[#bd9f67]/30' 
              : 'bg-white/5 text-white/80 border-white/10'
          }`}
        >
          {getTypeLabel(track.type || 'song')}
        </span>
        {track.trackNumber && (
          <span
            className={`text-xs font-semibold px-2.5 py-0.5 rounded-full border ${
              isCurrent 
                ? 'bg-[#bd9f67]/20 text-[#bd9f67] border-[#bd9f67]/30' 
                : 'bg-white/5 text-white/70 border-white/10'
            }`}
          >
            #{track.trackNumber}
          </span>
        )}
        {purchased && (
          <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-[#bd9f67]/20 border border-[#bd9f67]/30 text-[#bd9f67] flex items-center gap-1">
            <CheckCircle size={12} />
            Owned
          </span>
        )}
      </div>

      {/* Track info */}
      <div className="flex items-center gap-3">
        <div
          className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0 transition-transform duration-300 hover:scale-105 border border-white/10"
          style={{ background: 'linear-gradient(135deg, #1f2029, #121318)', boxShadow: '0 4px 12px rgba(0,0,0,0.5)' }}
        >
          <Disc3 size={28} className="text-[#bd9f67]" />
        </div>
        <div className="flex-grow min-w-0">
          <h3 className={`text-[1.05em] font-semibold whitespace-nowrap text-ellipsis overflow-hidden transition-colors ${
            isCurrent ? 'text-[#bd9f67]' : 'text-white/90'
          }`}>
            {track.name}
          </h3>
          <p className="text-[0.85em] text-white/60 mt-0.5 truncate">{track.artist || 'The Asidors'}</p>
          {track.album && !groupByAlbum && (
            <p className="text-xs text-white/40 truncate mt-0.5 hidden sm:block">{track.album}</p>
          )}
        </div>
        {/* Equalizer bars */}
        <div className="flex items-end gap-[2px] w-[38px] h-[32px] flex-shrink-0" aria-hidden="true">
          {EQ_BARS.map((delay, i) => (
            <span
              key={i}
              className={`eq-bar w-[3px] rounded-[2px] bg-gradient-to-t from-[#bd9f67] to-[#d0b07a] ${
                isCurrent && isPlaying ? '' : 'eq-paused'
              }`}
              style={{ height: 8, animationDelay: `${delay}s` }}
            />
          ))}
        </div>
      </div>

      {/* Playback controls */}
      <div className="flex flex-col gap-2">
        <div className="flex justify-between text-xs text-white/40 tabular-nums font-mono">
          <span>{currentLabel}</span>
          <span>-{remainingLabel}</span>
        </div>

        <div
          onClick={handleSeek}
          className="relative w-full h-1.5 rounded-full overflow-hidden cursor-pointer bg-white/10 hover:bg-white/15 transition-all"
          role="slider"
          aria-label="Seek"
          aria-valuemin={0}
          aria-valuemax={player.duration || 0}
          aria-valuenow={isNowPlaying ? player.currentTime : 0}
        >
          <div
            className="absolute inset-y-0 left-0 rounded-full"
            style={{ width: `${progress}%`, background: 'linear-gradient(90deg, #bd9f67, #d0b07a)' }}
          />
          <div
            className="absolute top-1/2 -translate-y-1/2 w-2.5 h-2.5 bg-white rounded-full"
            style={{ left: `${progress}%`, boxShadow: '0 0 8px rgba(189,159,103,0.8)' }}
          />
        </div>

        <div className="flex items-center justify-center gap-6 pt-1">
          <button
            onClick={player.playPrev}
            className="p-2.5 rounded-full text-white/60 hover:bg-white/10 hover:text-white transition"
            aria-label="Previous track"
          >
            <SkipBack size={20} fill="currentColor" />
          </button>
          <button
            onClick={() => onPlayPause(track, queueIndex)}
            className={`w-12 h-12 rounded-full flex items-center justify-center transition transform hover:scale-105 shadow-md ${
              isCurrent && isPlaying
                ? 'bg-[#bd9f67] text-gray-950 font-bold'
                : 'bg-white/10 text-white hover:bg-white/20 border border-white/10'
            }`}
            aria-label={isCurrent && isPlaying ? 'Pause' : 'Play'}
          >
            {isCurrent && isPlaying ? (
              <Pause size={22} fill="currentColor" />
            ) : (
              <Play size={22} fill="currentColor" className="ml-0.5" />
            )}
          </button>
          <button
            onClick={player.playNext}
            className="p-2.5 rounded-full text-white/60 hover:bg-white/10 hover:text-white transition"
            aria-label="Next track"
          >
            <SkipForward size={20} fill="currentColor" />
          </button>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex items-center gap-2 pt-1 border-t border-white/5">
        {purchased && currentUser ? (
          <>
            <button
              onClick={() => onAddToPlaylist(track)}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-xl text-xs font-semibold transition bg-white/5 border border-white/10 text-white/90 hover:bg-white/10 hover:text-white"
            >
              <Plus size={14} />
              <span className="hidden sm:inline">Add</span>
            </button>

            <button
              onClick={() => handleDownload(track)}
              disabled={downloadingId === track.id}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-xl text-xs font-semibold transition bg-[#bd9f67] text-gray-950 hover:bg-[#d0b07a] disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              <Download size={14} />
              <span className="hidden sm:inline">
                {downloadingId === track.id ? 'Downloading...' : 'Download'}
              </span>
            </button>
          </>
        ) : !purchased && currentUser ? (
          <>
            <button
              onClick={() => onAddToPlaylist(track)}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-xl text-xs font-semibold transition bg-white/5 border border-white/10 text-white/90 hover:bg-white/10 hover:text-white"
            >
              <Plus size={14} />
              <span className="hidden sm:inline">Add</span>
            </button>

            <button
              onClick={() => onPurchase(track)}
              disabled={processingPayment === track.id}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-xl text-xs font-semibold transition bg-[#bd9f67] text-gray-950 hover:bg-[#d0b07a] disabled:opacity-50"
            >
              <ShoppingCart size={14} />
              <span className="hidden sm:inline">
                {processingPayment === track.id ? 'Processing...' : 'Buy'}
              </span>
            </button>
          </>
        ) : null}
      </div>
    </motion.div>
  );
};
